import {
    MAX_COMMENT_LENGTH,
    type TaskComment,
    type TaskWithOccurrences,
} from "@dashboard/api/task";
import { useTranslation } from "react-i18next";
import { userByIdOptions } from "@dashboard/api/queryApi";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faPen, faTrashCan } from "@fortawesome/sharp-regular-svg-icons";
import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "frontend-components";
import { TaskAbilityContext } from "@dashboard/casl/taskAbility";
import { useAbility } from "@casl/react";
import { subject } from "@casl/ability";
import { useUpdateTaskComment, useDeleteTaskComment } from "@dashboard/hooks/useTaskCommentQuery";
import { CharacterCount } from "@dashboard/components/CharacterCount";
import { ConfirmDeleteDialog } from "@dashboard/components/common/ConfirmDeleteDialog";

interface TaskCommentItemProps {
    comment: TaskComment;
    task: TaskWithOccurrences;
}

export function TaskCommentItem({ comment, task }: TaskCommentItemProps) {
    const { i18n, t } = useTranslation();

    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(comment.comment);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const ability = useAbility(TaskAbilityContext);
    const commentSubject = subject("TaskComment", { task, ...comment });
    const canUpdate = ability.can("update", commentSubject);
    const canDelete = ability.can("delete", commentSubject);

    const updateMutation = useUpdateTaskComment();
    const deleteMutation = useDeleteTaskComment();

    const { data: userData } = useQuery({
        ...userByIdOptions({ id: comment.createdById }),
        enabled: !!comment.createdById,
    });

    const createdAtDate = new Date(comment.createdAt);
    const formattedDate = createdAtDate.toLocaleDateString(
        i18n.language === "nb" ? "nb-NO" : "en-US",
        {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        },
    );

    const authorName = userData
        ? `${userData.firstName} ${userData.lastName}`
        : t("common.loading");

    const isEdited = comment.updatedAt !== comment.createdAt;

    const handleSaveEdit = async () => {
        if (!editText.trim() || editText.length > MAX_COMMENT_LENGTH) return;
        try {
            await updateMutation.mutateAsync({
                taskId: task.id,
                commentId: comment.id,
                data: { comment: editText },
            });
            setIsEditing(false);
        } catch (error) {
            console.error("Failed to update comment:", error);
        }
    };

    const handleCancelEdit = () => {
        setEditText(comment.comment);
        setIsEditing(false);
    };

    const handleDelete = async () => {
        await deleteMutation.mutateAsync({
            taskId: task.id,
            commentId: comment.id,
        });
    };

    return (
        <div className="bg-surface-subtle border border-compendia-border rounded-[3px] p-3 flex flex-col gap-2">
            {isEditing ? (
                <>
                    <textarea
                        className="w-full p-3 bg-compendia-card border border-compendia-border rounded-none text-sm text-compendia-foreground resize-none focus:outline-none focus:border-border-focus"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={2}
                        autoFocus
                        maxLength={MAX_COMMENT_LENGTH}
                        aria-label={t("list.editComment")}
                    />
                    <div className="flex justify-between items-center">
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="submit"
                                size="submit"
                                onClick={handleSaveEdit}
                                disabled={
                                    !editText.trim() ||
                                    editText.length > MAX_COMMENT_LENGTH ||
                                    updateMutation.isPending
                                }
                            >
                                {t("common.save")}
                            </Button>
                            <Button type="button" variant="outline" onClick={handleCancelEdit}>
                                {t("common.cancel")}
                            </Button>
                        </div>
                        <CharacterCount
                            currentLength={editText.length}
                            maxLength={MAX_COMMENT_LENGTH}
                        />
                    </div>
                </>
            ) : (
                <div className="flex justify-between items-start gap-2">
                    <p className="text-sm text-compendia-foreground whitespace-pre-wrap break-words hyphens-auto">
                        {comment.comment}
                    </p>
                    {(canUpdate || canDelete) && (
                        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                            <DropdownMenuTrigger
                                className="text-compendia-muted hover:text-compendia-foreground p-1 -mr-1 -mt-1 shrink-0 cursor-pointer"
                                aria-label={t("common.optionsMenu")}
                            >
                                <FontAwesomeIcon icon={faEllipsisVertical} />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {canUpdate && (
                                    <DropdownMenuItem
                                        onClick={() => {
                                            setEditText(comment.comment);
                                            setIsEditing(true);
                                            setDropdownOpen(false);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPen} className="mr-2" />
                                        {t("common.edit")}
                                    </DropdownMenuItem>
                                )}
                                {canDelete && (
                                    <DropdownMenuItem
                                        onClick={() => {
                                            setDropdownOpen(false);
                                            setConfirmDeleteOpen(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrashCan} className="mr-2" />
                                        {t("common.delete")}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                </div>
            )}
            <div className="flex justify-between items-center text-xs text-compendia-muted">
                <span className="font-medium">
                    {authorName}
                    {isEdited && <span className="ml-1 italic">({t("list.edited")})</span>}
                </span>
                <span>{formattedDate}</span>
            </div>

            <ConfirmDeleteDialog
                open={confirmDeleteOpen}
                onOpenChange={setConfirmDeleteOpen}
                onConfirm={handleDelete}
                title={t("list.deleteComment")}
                description={t("list.confirmDeleteComment")}
            />
        </div>
    );
}
